(function () {
  function initializeAllTabs() {
    document.querySelectorAll('.tabs[data-tabs-id]').forEach((container) => {
      if (!container.dataset.tabsInitialized) {
        initTabs(container);
      }
    });
  }

  function reinitializeTabs(container) {
    // Abort previous event listeners via AbortController
    if (container._tabsAbortController) {
      container._tabsAbortController.abort();
    }

    // Remove initialized flag
    delete container.dataset.tabsInitialized;
    container.classList.remove('is-loaded');

    // Move tabs back out of the header wrapper and remove it so initTabs can rebuild
    const oldHeader = container.querySelector('.tabs__header');
    if (oldHeader) {
      const tabs = Array.from(oldHeader.querySelectorAll('.tab'));
      tabs.forEach((tab) => {
        // Reset data attributes set by initTabs
        delete tab.dataset.tabsId;
        delete tab.dataset.tabContent;
        delete tab.dataset.blockId;
        tab.removeAttribute('open');
        container.insertBefore(tab, oldHeader);
      });
      oldHeader.remove();
    }

    // Reset tab content classes
    container.querySelectorAll('.tab__content').forEach((content) => {
      content.classList.remove('is-active');
      Array.from(content.classList)
        .filter((cls) => cls.startsWith('tab__content--'))
        .forEach((cls) => content.classList.remove(cls));
    });

    initTabs(container);
  }

  // Initialize all tabs containers
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAllTabs);
  } else {
    initializeAllTabs();
  }

  // Helper: lock scroll position to prevent editor's scrollIntoView
  function lockScrollBriefly() {
    const scrollY = window.scrollY;
    const lockScroll = () => window.scrollTo(0, scrollY);
    window.addEventListener('scroll', lockScroll);
    setTimeout(() => {
      window.removeEventListener('scroll', lockScroll);
      window.scrollTo(0, scrollY);
    }, 300);
  }

  // Helper: forcefully switch to a specific tab, bypassing toggle handler
  function forceActivateTab(tabsContainer, targetTab) {
    const allTabs = Array.from(tabsContainer.querySelectorAll('.tabs__header .tab, :scope > .tab'));
    const allContents = Array.from(tabsContainer.querySelectorAll('.tab__content'));

    // Close all tabs and hide all content
    allTabs.forEach((t, index) => {
      if (t !== targetTab) {
        t.removeAttribute('open');
      }
      const content = allContents[index];
      if (content) content.classList.remove('is-active');
    });

    // Open target tab and show its content
    targetTab.setAttribute('open', '');
    const targetIndex = allTabs.indexOf(targetTab);
    if (targetIndex >= 0 && allContents[targetIndex]) {
      allContents[targetIndex].classList.add('is-active');
    }
  }

  // Shopify Theme Editor integration
  // These events only fire inside the editor, so no need to gate on designMode
  document.addEventListener('shopify:block:select', function (event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if the selected block is inside a tabs container at all
    const tabsContainer = event.target.closest('.tabs[data-tabs-id]');
    if (!tabsContainer) return;

    // Ensure tabs are initialized before trying to find/open them
    if (!tabsContainer.dataset.tabsInitialized) {
      initTabs(tabsContainer);
    }

    // Prevent the editor from scrolling to the block inside tabs
    lockScrollBriefly();

    // Check if the selected block is inside a tab content area
    const tabContent = event.target.closest('.tab__content');
    if (tabContent) {
      // Find which tab this content belongs to by matching index
      const allContents = Array.from(tabsContainer.querySelectorAll('.tab__content'));
      const contentIndex = allContents.indexOf(tabContent);

      const allTabs = Array.from(tabsContainer.querySelectorAll('.tabs__header .tab, :scope > .tab'));
      if (contentIndex >= 0 && contentIndex < allTabs.length) {
        forceActivateTab(tabsContainer, allTabs[contentIndex]);
      }
      return;
    }

    // Check if the selected block IS a tab (details element) or is inside one
    const tab = event.target.closest('.tab');
    if (tab) {
      forceActivateTab(tabsContainer, tab);
      return;
    }

    // Try to find which tab contains this element
    const allTabs = Array.from(tabsContainer.querySelectorAll('.tabs__header .tab, :scope > .tab'));
    for (const t of allTabs) {
      if (t.contains(event.target) || event.target.contains(t)) {
        forceActivateTab(tabsContainer, t);
        return;
      }
    }
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (!(event.target instanceof HTMLElement)) return;
    const containers = event.target.querySelectorAll('.tabs[data-tabs-id]');
    containers.forEach((container) => {
      if (container.dataset.tabsInitialized) {
        reinitializeTabs(container);
      } else {
        initTabs(container);
      }
    });
  });

  document.addEventListener('shopify:block:add', function (event) {
    if (!(event.target instanceof HTMLElement)) return;
    const tabsContainer = event.target.closest('.tabs[data-tabs-id]')
      || event.target.querySelector('.tabs[data-tabs-id]');
    if (tabsContainer) {
      reinitializeTabs(tabsContainer);
    }
  });

  document.addEventListener('shopify:block:remove', function (event) {
    if (!(event.target instanceof HTMLElement)) return;
    const tabsContainer = event.target.closest('.tabs[data-tabs-id]')
      || event.target.querySelector('.tabs[data-tabs-id]');
    if (tabsContainer) {
      reinitializeTabs(tabsContainer);
    }
  });

  // MutationObserver fallback: detect when new .tab elements are added to a .tabs container
  // but are not inside the .tabs__header (meaning JS hasn't initialized them yet)
  let isReinitializing = false;
  const pendingReinits = new Set();
  let reinitTimeout = null;

  function scheduleReinit(tabsContainer) {
    if (isReinitializing) return;
    pendingReinits.add(tabsContainer);
    if (reinitTimeout) return;
    reinitTimeout = setTimeout(() => {
      reinitTimeout = null;
      isReinitializing = true;
      pendingReinits.forEach((container) => {
        if (container.isConnected) {
          reinitializeTabs(container);
        }
      });
      pendingReinits.clear();
      isReinitializing = false;
    }, 50);
  }

  const tabsObserver = new MutationObserver(function (mutations) {
    if (isReinitializing) return;

    for (const mutation of mutations) {
      if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) continue;

      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;

        // Check if a .tab element was added directly to a .tabs container (not inside header)
        if (node.classList.contains('tab') && node.parentElement?.matches('.tabs[data-tabs-id]')) {
          const tabsContainer = node.parentElement;
          if (tabsContainer.dataset.tabsInitialized) {
            scheduleReinit(tabsContainer);
          }
        }

        // Check if a .tab__content was added directly to a .tabs container
        if (node.classList.contains('tab__content') && node.parentElement?.matches('.tabs[data-tabs-id]')) {
          const tabsContainer = node.parentElement;
          if (tabsContainer.dataset.tabsInitialized) {
            scheduleReinit(tabsContainer);
          }
        }

        // Check if any descendant contains a new uninitialized tabs container (for section reloads)
        if (node.querySelector) {
          node.querySelectorAll('.tabs[data-tabs-id]:not([data-tabs-initialized])').forEach((container) => {
            initTabs(container);
          });
        }
      }
    }
  });

  tabsObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  function initTabs(tabsContainer) {
    // Skip if already initialized
    if (tabsContainer.dataset.tabsInitialized) return;
    tabsContainer.dataset.tabsInitialized = 'true';

    // Create an AbortController to manage event listeners for cleanup
    const controller = new AbortController();
    tabsContainer._tabsAbortController = controller;
    const signal = controller.signal;

    // Get the tabs ID from the container
    const tabsId = tabsContainer.dataset.tabsId;

    // Wrap tabs in a scrollable header container if not already wrapped
    let tabsHeader = tabsContainer.querySelector('.tabs__header');
    const tabs = Array.from(tabsContainer.querySelectorAll(':scope > .tab'));
    if (!tabsHeader && tabs.length > 0) {
      tabsHeader = document.createElement('div');
      tabsHeader.className = 'tabs__header';
      tabs[0].parentNode.insertBefore(tabsHeader, tabs[0]);
      tabs.forEach((tab) => tabsHeader.appendChild(tab));
    }

    if (!tabsHeader) {
      // No tabs found, nothing to initialize
      return;
    }

    // Also grab any tabs already inside an existing header
    const allTabs = Array.from(tabsHeader.querySelectorAll('.tab'));

    // Create sliding indicator line if it doesn't exist
    let indicator = tabsHeader.querySelector('.tabs__indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'tabs__indicator';
      tabsHeader.appendChild(indicator);
    }

    // Function to update indicator position
    function updateIndicator() {
      // Find active tab: either regular tab with [open] or link tab with .tab--current
      const activeTab = tabsHeader.querySelector('.tab[open]') || tabsHeader.querySelector('.tab--link.tab--current');
      if (activeTab && indicator) {
        // Get the element to measure: summary for regular tabs, .tab__link for link tabs
        const targetElement = activeTab.querySelector('summary') || activeTab.querySelector('.tab__link');
        if (targetElement) {
          const headerRect = tabsHeader.getBoundingClientRect();
          const targetRect = targetElement.getBoundingClientRect();
          // Account for scroll offset in the header
          const left = targetRect.left - headerRect.left + tabsHeader.scrollLeft;
          const width = targetRect.width;
          indicator.style.left = left + 'px';
          indicator.style.width = width + 'px';
        }
      }
    }

    const tabContents = Array.from(tabsContainer.querySelectorAll('.tab__content'));

    // Initialize each tab with its index and associate with container
    allTabs.forEach((tab, index) => {
      const tabIndex = index + 1;
      const content = tabContents[index];

      // Associate tab with container
      tab.dataset.tabsId = tabsId;

      // Set --tab-index for ordering
      tab.style.setProperty('--tab-index', tabIndex);
      if (content) {
        content.style.setProperty('--tab-index', tabIndex);
      }

      // Extract blockId from tab classes
      const tabClasses = tab.className.split(' ').filter((cls) => cls !== 'tab' && !cls.startsWith('tab--'));
      const blockId = tabClasses.find((cls) => cls.startsWith('_tab_')) || tabClasses[0];

      if (blockId) {
        tab.dataset.tabContent = `tab__content--${blockId}`;
        tab.dataset.blockId = blockId;
        if (content) {
          content.classList.add(`tab__content--${blockId}`);
        }
      }
    });

    // Flag to prevent multiple toggle events from firing simultaneously
    let isHandlingToggle = false;
    // Track scroll lock to prevent native <details> scroll-into-view
    let scrollLockCleanup = null;

    // Handle tab switching using toggle event for better compatibility
    allTabs.forEach((tab) => {
      const summary = tab.querySelector('summary');
      const blockId = tab.dataset.blockId;
      const content = blockId ? tabsContainer.querySelector(`.tab__content--${blockId}`) : null;
      const isDisabled = tab.dataset.disabled === 'true';

      if (summary) {
        // Prevent closing current tab on click and save scroll position
        summary.addEventListener('click', (e) => {
          if (tab.dataset.tabsId === tabsId && tab.hasAttribute('open')) {
            e.preventDefault();
            return;
          }
          // Prevent opening disabled tabs
          if (isDisabled && !tab.hasAttribute('open')) {
            e.preventDefault();
            return;
          }
          // Lock scroll position before the browser's native scroll-into-view
          const scrollY = window.scrollY;
          const lockScroll = () => window.scrollTo(0, scrollY);
          window.addEventListener('scroll', lockScroll);
          scrollLockCleanup = () => {
            window.removeEventListener('scroll', lockScroll);
            window.scrollTo(0, scrollY);
          };
          // Safety timeout to ensure cleanup
          setTimeout(() => {
            if (scrollLockCleanup) {
              scrollLockCleanup();
              scrollLockCleanup = null;
            }
          }, 100);
        }, { signal });
      }

      // Use toggle event to sync content visibility and close other tabs
      tab.addEventListener('toggle', (e) => {
        // Only process if this tab belongs to this container
        if (tab.dataset.tabsId !== tabsId) return;

        // Skip if we're already handling a toggle (prevents cascade of events)
        if (isHandlingToggle) return;

        // Prevent opening disabled tabs
        if (isDisabled && tab.open) {
          e.preventDefault();
          tab.removeAttribute('open');
          return;
        }

        if (tab.open) {
          isHandlingToggle = true;

          if (content) content.classList.add('is-active');

          // Close all other tabs in this container
          allTabs.forEach((otherTab) => {
            if (otherTab !== tab && otherTab.open) {
              otherTab.removeAttribute('open');
              const otherBlockId = otherTab.dataset.blockId;
              if (otherBlockId) {
                const otherContent = tabsContainer.querySelector(`.tab__content--${otherBlockId}`);
                if (otherContent) otherContent.classList.remove('is-active');
              }
            }
          });

          // Release scroll lock if active
          if (scrollLockCleanup) {
            scrollLockCleanup();
            scrollLockCleanup = null;
          }

          // Update indicator position
          setTimeout(updateIndicator, 0);

          // Reset flag
          setTimeout(() => {
            isHandlingToggle = false;
          }, 50);
        } else {
          if (!isHandlingToggle && content) {
            content.classList.remove('is-active');
          }
        }
      }, { signal });
    });

    // Ensure at least one tab is open (skip link tabs - they use .tab--current instead)
    let hasOpenTab = false;
    allTabs.forEach((tab) => {
      // Skip link tabs - they don't use open attribute
      if (tab.classList.contains('tab--link')) return;

      if (tab.hasAttribute('open') || tab.open) {
        hasOpenTab = true;
        const blockId = tab.dataset.blockId;
        if (blockId) {
          const content = tabsContainer.querySelector(`.tab__content--${blockId}`);
          if (content) content.classList.add('is-active');
        }
      }
    });

    // If no tabs are open, open the first non-disabled, non-link one
    if (!hasOpenTab && allTabs.length > 0) {
      let firstTab = null;
      for (const tab of allTabs) {
        // Skip link tabs - they don't use open attribute
        if (tab.classList.contains('tab--link')) continue;
        if (tab.dataset.disabled !== 'true') {
          firstTab = tab;
          break;
        }
      }

      if (firstTab) {
        const firstBlockId = firstTab.dataset.blockId;
        if (firstBlockId) {
          const firstContent = tabsContainer.querySelector(`.tab__content--${firstBlockId}`);
          if (firstContent) {
            firstTab.setAttribute('open', '');
            firstContent.classList.add('is-active');
          }
        }
      }
    }

    // Initialize indicator position
    updateIndicator();

    // Update indicator on window resize
    window.addEventListener('resize', () => {
      updateIndicator();
    }, { signal });

    // Add loaded class to indicate initialization is complete
    tabsContainer.classList.add('is-loaded');
  }
})();
